/**
 * remark plugin: turns citation markers in text nodes into `citation` nodes that render as
 * <cite-ref ids="1,3"> (mapped to CitationGroup by the markdown components).
 *
 *  - Only `text` nodes are visited; `code` and `inlineCode` are separate node types and stay untouched.
 *  - Adjacent markers merge into one group: "[1][3]", "[1, 3]" and "[1、3]" all give ids "1,3".
 *  - Only ids present in `sourceById` are kept. A marker group with no known id is removed (deltas can carry
 *    ids the server later drops from `answer_end`, e.g. "[9]" with 4 sources); with no sources at all the
 *    text is left untouched.
 */
import { SKIP, visit } from 'unist-util-visit';

// One or more adjacent markers; each marker holds 1-2 digit ids separated by "," or "、".
export const CITATION_RUN_RE = /(?:\[\d{1,2}(?:\s*[,、]\s*\d{1,2})*\]\s?)*\[\d{1,2}(?:\s*[,、]\s*\d{1,2})*\]/g;

/** Ids of a marker run, in order, de-duplicated. "[1][3, 1]" -> [1, 3] */
export function parseCitationIds(run) {
  const ids = [];
  (run.match(/\d{1,2}/g) || []).forEach((d) => {
    const n = Number(d);
    if (!ids.includes(n)) ids.push(n);
  });
  return ids;
}

/** Ids cited in a markdown string, in first-appearance order (code spans and fences excluded). */
export function citedIdsInText(text, sourceById) {
  const out = [];
  const withoutCode = String(text || '')
    .replace(/```[\s\S]*?(```|$)/g, ' ')
    .replace(/`[^`\n]*`/g, ' ');
  const matches = withoutCode.match(CITATION_RUN_RE) || [];
  matches.forEach((run) => {
    parseCitationIds(run).forEach((id) => {
      if ((!sourceById || sourceById[id]) && !out.includes(id)) out.push(id);
    });
  });
  return out;
}

export function splitCitations(value, sourceById) {
  const parts = [];
  let last = 0;
  let matched = false;
  CITATION_RUN_RE.lastIndex = 0;
  let match = CITATION_RUN_RE.exec(value);
  while (match) {
    matched = true;
    const ids = parseCitationIds(match[0]).filter((id) => sourceById && sourceById[id]);
    if (match.index > last) parts.push({ type: 'text', value: value.slice(last, match.index) });
    if (ids.length) {
      parts.push({
        type: 'citation',
        data: { hName: 'cite-ref', hProperties: { ids: ids.join(',') } },
        children: [],
      });
    }
    last = match.index + match[0].length;
    match = CITATION_RUN_RE.exec(value);
  }
  if (!matched) return null;
  if (last < value.length) parts.push({ type: 'text', value: value.slice(last) });
  return parts;
}

export default function remarkCitations(options = {}) {
  const { sourceById } = options;
  return (tree) => {
    if (!sourceById || Object.keys(sourceById).length === 0) return;
    visit(tree, 'text', (node, index, parent) => {
      if (!parent || index == null || !node.value || node.value.indexOf('[') === -1) return undefined;
      const parts = splitCitations(node.value, sourceById);
      if (!parts) return undefined;
      parent.children.splice(index, 1, ...parts);
      return [SKIP, index + parts.length];
    });
  };
}
